import React, { useCallback, useEffect, useRef, useState } from 'react'
import { getErrorMessage } from '@/utils/getErrorMessage'
import {
  Typography,
  Button,
  Breadcrumb,
  Layout,
  Flex,
  Card,
  Form,
  Input,
  Select,
  Spin,
  message,
} from 'antd'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeftOutlined, SearchOutlined } from '@ant-design/icons'
import { type Permission, type Role } from '@/data'
import { getAllPermissions } from '@/api/permissions/getPermissions'
import { getRoles } from '@/api/roles/getRoles'
import { getUser } from '@/api/users/getUser'
import { createUser } from '@/api/users/createUser'
import { updateUser } from '@/api/users/updateUser'
import { permissionTagRender } from '@/components/PermissionTag/utils'
import {
  sortOptionsByLabel,
  sortSelectedValues,
  type SelectOption,
} from '@/utils/selectSorting'
import { usePartnerSearch } from '@/utils/usePartnerSearch'

const { Title } = Typography
const { Content } = Layout

const UserEditorContent: React.FC = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()

  const isEditMode = Boolean(id)

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [partnerIds, setPartnerIds] = useState<number[]>([])
  const [selectedPartnerOptions, setSelectedPartnerOptions] = useState<
    SelectOption[]
  >([])
  const [roleIds, setRoleIds] = useState<number[]>([])
  const [permissionIds, setPermissionIds] = useState<number[]>([])
  const [roleOptions, setRoleOptions] = useState<SelectOption[]>([])
  const [permissionOptions, setPermissionOptions] = useState<SelectOption[]>([])

  const {
    options: searchPartnerOptions,
    loading: partnersLoading,
    onSearch: onPartnerSearch,
    loadMore: loadMorePartners,
  } = usePartnerSearch()

  // Merge pre-selected partner options with search results so tags always render
  const mergedPartnerOptions = React.useMemo(() => {
    const searchIds = new Set(searchPartnerOptions.map((o) => o.value))
    const missing = selectedPartnerOptions.filter(
      (o) => !searchIds.has(o.value),
    )
    return [...missing, ...searchPartnerOptions]
  }, [searchPartnerOptions, selectedPartnerOptions])

  const handlePartnerScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const target = e.target as HTMLDivElement
      if (target.scrollTop + target.clientHeight >= target.scrollHeight - 40) {
        loadMorePartners()
      }
    },
    [loadMorePartners],
  )

  const hasFetched = useRef(false)

  useEffect(() => {
    if (hasFetched.current) return
    hasFetched.current = true
    ;(async () => {
      try {
        setLoading(true)
        const [{ data: roles }, { data: permissions }, user] =
          await Promise.all([
            getRoles(),
            getAllPermissions().then((data) => ({ data })),
            id ? getUser(id) : Promise.resolve(null),
          ])

        const sortedRoleOptions = sortOptionsByLabel(
          roles.map((role: Role) => ({
            label: role.name,
            value: role.id,
          })),
        )

        setRoleOptions(sortedRoleOptions)
        const sortedPermissionOptions = sortOptionsByLabel(
          permissions.map((permission: Permission) => ({
            label: permission.name,
            value: permission.id,
          })),
        )

        setPermissionOptions(sortedPermissionOptions)

        if (!user) return

        setName(user.name)
        setEmail(user.email)
        setPartnerIds(user.partners.map((p) => p.id))
        setSelectedPartnerOptions(
          user.partners.map((p) => ({
            label: `${p.name} (${p.affiliate_id})`,
            value: p.id,
          })),
        )
        setRoleIds(sortSelectedValues(user.roleIds, sortedRoleOptions))
        setPermissionIds(
          sortSelectedValues(user.permissionIds, sortedPermissionOptions),
        )
      } catch (err) {
        message.error('Failed to load user')
        console.error(err)
      } finally {
        setLoading(false)
      }
    })()
  }, [id])

  const handleSave = async () => {
    try {
      setSaving(true)
      if (isEditMode && id) {
        await updateUser(id, {
          name,
          email,
          partner_ids: partnerIds,
          role_ids: roleIds,
          permission_ids: [...new Set(permissionIds)],
          ...(password ? { password } : {}),
        })
        message.success('User updated')
      } else {
        const created = await createUser({
          name,
          email,
          password,
          partner_ids: partnerIds,
        })
        if (roleIds.length > 0 || permissionIds.length > 0) {
          await updateUser(String(created.id), {
            role_ids: roleIds,
            permission_ids: [...new Set(permissionIds)],
          })
        }
        message.success('User created')
        navigate(`/users/${created.id}/edit`)
      }
    } catch (err) {
      const fallback = isEditMode
        ? 'Failed to update user'
        : 'Failed to create user'
      message.error(getErrorMessage(err, fallback))
      console.error(err)
    } finally {
      setSaving(false)
    }
  }

  const handlePermissionChange = (values: number[]) => {
    setPermissionIds(sortSelectedValues(values, permissionOptions))
  }

  const handleRoleChange = (values: number[]) => {
    setRoleIds(sortSelectedValues(values, roleOptions))
  }

  if (loading) {
    return (
      <Flex justify="center" align="center" style={{ minHeight: 400 }}>
        <Spin size="large" />
      </Flex>
    )
  }

  return (
    <div>
      <Flex vertical gap="middle" style={{ width: '100%' }}>
        <Breadcrumb
          items={[
            { title: 'Users', href: '/users' },
            { title: isEditMode ? `Edit: ${name}` : 'New User' },
          ]}
        />

        <Flex align="center" gap="middle">
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/users')}
          />
          <Title level={3} style={{ margin: 0 }}>
            {isEditMode ? `Edit User: ${name}` : 'Create New User'}
          </Title>
        </Flex>

        <Flex gap="large" style={{ marginTop: 16 }}>
          <Content style={{ flex: 1, minHeight: 400 }}>
            <Card title="User Info" style={{ maxWidth: 700 }}>
              <Form layout="vertical">
                <Form.Item label="Name">
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Enter name"
                  />
                </Form.Item>
                <Form.Item label="Email">
                  <Input
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter email"
                  />
                </Form.Item>
                <Form.Item label={isEditMode ? 'New Password' : 'Password'}>
                  <Input.Password
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={
                      isEditMode
                        ? 'Leave blank to keep current'
                        : 'Enter password'
                    }
                  />
                </Form.Item>
                <Form.Item label="Partners">
                  <Select<number[]>
                    mode="multiple"
                    showSearch
                    value={partnerIds}
                    onChange={setPartnerIds}
                    onSearch={onPartnerSearch}
                    onPopupScroll={handlePartnerScroll}
                    options={mergedPartnerOptions}
                    placeholder="Search partners by name or ID..."
                    suffixIcon={<SearchOutlined />}
                    filterOption={false}
                    loading={partnersLoading}
                    style={{ width: '100%' }}
                    notFoundContent={
                      partnersLoading ? <Spin size="small" /> : 'No matches'
                    }
                    dropdownRender={(menu) => (
                      <>
                        {menu}
                        {partnersLoading && mergedPartnerOptions.length > 0 && (
                          <Flex justify="center" style={{ padding: 8 }}>
                            <Spin size="small" />
                          </Flex>
                        )}
                      </>
                    )}
                  />
                </Form.Item>
                <Form.Item label="Roles">
                  <Select<number[]>
                    mode="multiple"
                    value={roleIds}
                    onChange={handleRoleChange}
                    options={roleOptions}
                    placeholder="Select roles"
                    optionFilterProp="label"
                    style={{ width: '100%' }}
                  />
                </Form.Item>
                <Form.Item label="Permissions">
                  <Select<number[]>
                    mode="multiple"
                    value={permissionIds}
                    onChange={handlePermissionChange}
                    options={permissionOptions}
                    placeholder="Select permissions"
                    optionFilterProp="label"
                    tagRender={permissionTagRender}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Form>
            </Card>
            <Flex justify="flex-end" style={{ paddingTop: 16 }}>
              <Button type="primary" onClick={handleSave} loading={saving}>
                {isEditMode ? 'Save Changes' : 'Create User'}
              </Button>
            </Flex>
          </Content>
        </Flex>
      </Flex>
    </div>
  )
}

export const UserEditor: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  return <UserEditorContent key={id ?? 'new'} />
}
